import React, { useState } from 'react';
import { CheckCircle, ArrowRight, Star, Award, Shield } from 'lucide-react';
import { createCheckout } from '../services/api';
import PreCheckoutModal from '../components/PreCheckoutModal';
import { toast } from 'sonner';

const formulas = [
  {
    id: 'oral-bronze',
    name: 'BRONZE',
    icon: Shield,
    subtitle: 'Pour structurer et sécuriser son oral',
    price: 160,
    paymentNote: 'Paiement unique ou en 3x',
    features: [
      'Lecture complète du livret 2',
      'Analyse de la cohérence globale',
      'Travail sur la trame de présentation personnelle',
      '1 visio de 45 minutes (questions / posture / conseils jury)'
    ],
    footer: 'Idéal pour les candidats qui veulent cibler les essentiels',
    headerBg: 'linear-gradient(135deg, #d4a574, #c4956a)',
    accentColor: '#8B6914',
    borderColor: '#d4a574',
    priceColor: '#8B6914',
    highlight: false
  },
  {
    id: 'oral-silver',
    name: 'SILVER',
    icon: Star,
    subtitle: 'Pour gagner en aisance et en impact',
    price: 240,
    paymentNote: 'Paiement unique ou en 3x',
    features: [
      'Lecture complète du livret 2',
      'Analyse points forts / axes d\'amélioration',
      'Travail approfondi sur la présentation personnelle',
      'Préparation aux questions potentielles du jury',
      '1 oral blanc (1h) + débrief détaillé'
    ],
    footer: 'Un accompagnement complet pour réussir votre oral',
    headerBg: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
    accentColor: '#8e44ad',
    borderColor: '#9b59b6',
    priceColor: '#8e44ad',
    highlight: true
  },
  {
    id: 'oral-gold',
    name: 'GOLD',
    icon: Award,
    subtitle: 'Préparation complète et sécurisation maximale',
    price: 360,
    paymentNote: 'Paiement unique ou en 3x',
    features: [
      'Lecture complète du livret 2',
      'Analyse approfondie posture professionnelle',
      'Optimisation de la présentation personnelle',
      'Préparation ciblée aux questions jury',
      '2 oraux blancs (1h chacun)',
      'Débrief détaillé'
    ],
    footer: 'L\'accompagnement premium pour maximiser vos chances de réussite',
    headerBg: 'linear-gradient(135deg, #d4af37, #b8960c)',
    accentColor: '#b8960c',
    borderColor: '#d4af37',
    priceColor: '#b8960c',
    highlight: false
  }
];

export default function OralVAEPage() {
  const [loading, setLoading] = useState(null);
  const [checkoutModal, setCheckoutModal] = useState(false);
  const [pendingFormula, setPendingFormula] = useState(null);

  const handleBuy = async (formula) => {
    setPendingFormula(formula);
    setCheckoutModal(true);
  };

  const handleConfirm = async ({ nom, prenom, email, telephone }) => {
    setCheckoutModal(false);
    setLoading(pendingFormula.id);
    try {
      const data = await createCheckout({
        programId: 'oral-vae',
        formula: 'kit',
        kitId: pendingFormula.id,
        kitTitle: `Oral VAE — Formule ${pendingFormula.name}`,
        price: pendingFormula.price,
        origin: window.location.origin,
        email, nom, prenom, telephone
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error("Erreur lors de la création du paiement. Veuillez réessayer.");
    }
    setLoading(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-main)', paddingTop: '80px' }}>
      <div className="container" style={{ maxWidth: '1100px', margin: '0 auto', padding: '3rem 1.5rem' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h1 className="heading-1" style={{ marginBottom: '0.75rem' }}>
            Accompagnement Oral VAE
          </h1>
          <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', maxWidth: '650px', margin: '0 auto 0.75rem' }}>
            Pour tous les diplômes — Une fois le livret 2 déposé
          </p>
          <p style={{
            display: 'inline-block',
            background: '#FEF3C7', color: '#92400E',
            padding: '0.5rem 1.25rem', borderRadius: '2rem',
            fontWeight: 600, fontSize: '0.85rem'
          }}>
            Lecture complète du livret 2 incluse dans chaque formule
          </p>
        </div>

        {/* 3 Formulas - card style like image */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
          gap: '1.5rem',
          alignItems: 'start',
          marginBottom: '3rem'
        }}>
          {formulas.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.id}
                data-testid={`oral-formula-${f.id}`}
                style={{
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  border: f.highlight ? `3px solid ${f.borderColor}` : `2px solid ${f.borderColor}40`,
                  background: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  transform: f.highlight ? 'scale(1.03)' : 'none',
                  boxShadow: f.highlight ? `0 12px 40px ${f.borderColor}30` : '0 2px 12px rgba(0,0,0,0.06)',
                  position: 'relative'
                }}
              >
                {/* Recommandé badge */}
                {f.highlight && (
                  <div style={{
                    background: f.headerBg,
                    color: 'white',
                    textAlign: 'center',
                    padding: '0.4rem',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                  }}>
                    Recommandée
                  </div>
                )}

                {/* Header with name */}
                <div style={{
                  background: f.headerBg,
                  padding: '1rem 1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem'
                }}>
                  <Icon size={22} color="white" />
                  <span style={{
                    color: 'white',
                    fontWeight: 800,
                    fontSize: '1.15rem',
                    letterSpacing: '0.05em'
                  }}>
                    {f.name}
                  </span>
                </div>

                {/* Body */}
                <div style={{ padding: '1.75rem 1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>

                  {/* Price */}
                  <p style={{
                    fontSize: '2.2rem',
                    fontWeight: 800,
                    color: f.priceColor,
                    marginBottom: '0.25rem',
                    lineHeight: 1
                  }}>
                    {f.price}€
                  </p>
                  <p style={{ fontSize: '0.8rem', color: '#9CA3AF', marginBottom: '1.5rem' }}>
                    {f.paymentNote}
                  </p>

                  {/* Features */}
                  <p style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    Cette formule comprend :
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', flex: 1, marginBottom: '1.75rem' }}>
                    {f.features.map((feat, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                        <CheckCircle size={17} style={{ flexShrink: 0, color: f.accentColor, marginTop: '2px' }} />
                        <span style={{ fontSize: '0.88rem', lineHeight: '1.4' }}>{feat}</span>
                      </div>
                    ))}
                  </div>

                  {/* Footer text */}
                  <p style={{
                    fontSize: '0.82rem',
                    fontStyle: 'italic',
                    color: f.accentColor,
                    marginBottom: '1.25rem',
                    lineHeight: '1.4'
                  }}>
                    {f.footer}
                  </p>

                  {/* CTA Button */}
                  <button
                    data-testid={`buy-${f.id}`}
                    disabled={loading === f.id}
                    onClick={() => handleBuy(f)}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s',
                      ...(f.highlight
                        ? {
                            background: f.headerBg,
                            color: 'white',
                            border: 'none'
                          }
                        : {
                            background: 'transparent',
                            color: f.accentColor,
                            border: `2px solid ${f.borderColor}`
                          })
                    }}
                  >
                    {loading === f.id ? 'Chargement...' : 'Choisir cette formule'}
                  </button>

                  {/* 3x payment note */}
                  <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.6rem' }}>
                    ou 3x{Math.round(f.price / 3)}€ sans frais
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info bottom */}
        <div style={{
          padding: '1.5rem 2rem', textAlign: 'center', borderRadius: '1rem',
          background: 'linear-gradient(135deg, #FFF7ED 0%, #fff 100%)',
          border: '2px solid #d4af3730'
        }}>
          <p style={{ fontWeight: 600, color: '#8B6914', marginBottom: '0.5rem' }}>
            Ces formules s'adressent aux candidats ayant déjà déposé leur Livret 2
          </p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Vous n'avez pas encore rédigé votre Livret 2 ? Découvrez nos accompagnements complets (Bronze, Silver, Gold) sur la page de chaque diplôme.
          </p>
        </div>

      </div>

      <PreCheckoutModal
        open={checkoutModal}
        onClose={() => setCheckoutModal(false)}
        onConfirm={handleConfirm}
        loading={!!loading}
      />
    </div>
  );
}
